import { SxProps, Theme } from '@mui/material/styles'
import { ScanRegion } from './scanRegion'

/**
 * Verdes reutilizados de InventoryCheckHandheld (InventoryCheckDetailScreen.tsx)
 * en lugar de depender de success.main, que hoy es el default de MUI porque
 * src/theme.tsx solo define primary y secondary.
 */
const GREEN = '#16a34a'
const GREEN_DARK = '#15803d'
const GREEN_BG = '#f0fdf4'
const GREEN_BG_HOVER = '#dcfce7'

export const cameraScanButtonSx: SxProps<Theme> = {
  maxWidth: 500, // alinea con el TextField y los dos Button vecinos de ScanStep
  /**
   * Se centra solo al topar el maxWidth.
   *
   * Sin esto depende de como aline el contenedor de cada pantalla: ScanStep usa
   * alignItems 'center' y quedaba bien, pero UnknownIdStep y ScanSearchPanel
   * estiran a sus hijos, asi que el boton topaba los 500px y quedaba pegado a la
   * izquierda en pantallas anchas.
   */
  mx: 'auto',
  // Mismo radio que el boton "No conozco el numero".
  borderRadius: '16px',
  // Casi el doble del alto por defecto de MUI (~36px): en el piso se toca con
  // guantes o a las apuradas, y en modo camara es la accion principal.
  minHeight: 64,
  fontSize: '16px',
  '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 26 },
  textTransform: 'none',
  fontWeight: 500,
  color: GREEN,
  borderColor: GREEN,
  // MUI resetea el borde a 1px en hover; sin repetir borderWidth se ve un salto de 0.5px
  borderWidth: '1.5px',
  backgroundColor: GREEN_BG,
  '&:hover': {
    borderWidth: '1.5px',
    borderColor: GREEN_DARK,
    backgroundColor: GREEN_BG_HOVER
  },
  // En touch no hay hover: el feedback tiene que venir del active
  '&:active': {
    backgroundColor: GREEN_BG_HOVER
  },
  '&.Mui-disabled': {
    color: '#9ca3af',
    borderColor: '#e5e7eb',
    backgroundColor: '#f9fafb'
  }
}

export const dialogPaperSx: SxProps<Theme> = {
  backgroundColor: '#000'
}

export const dialogHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 2,
  py: 1.5,
  color: '#fff',
  flexShrink: 0
}

export const scannerStageSx: SxProps<Theme> = {
  position: 'relative',
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  backgroundColor: '#000'
}

/**
 * El video se estira para cubrir el area disponible. Tiene que quedar visible y
 * con tamano no-cero: iOS pausa el playback en elementos con display none,
 * visibility hidden o ancho 0.
 */
export const videoSx: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
}

/**
 * Recuadro de guia. Se dimensiona con las MISMAS fracciones que usa
 * cropRectFor() para filtrar la lectura, asi que el area que el operario ve es
 * exactamente el area que se acepta.
 */
export const scannerFrameSx = (region: ScanRegion): SxProps<Theme> => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: `${region.widthRatio * 100}%`,
  height: `${region.heightRatio * 100}%`,
  border: `3px solid ${GREEN}`,
  borderRadius: '12px',
  boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.45)',
  pointerEvents: 'none'
})

export const helperTextSx: SxProps<Theme> = {
  position: 'absolute',
  bottom: 32,
  left: 0,
  right: 0,
  px: 3,
  textAlign: 'center',
  color: '#fff',
  textShadow: '0 1px 3px rgba(0,0,0,0.8)'
}

export const feedbackPanelSx: SxProps<Theme> = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  px: 4,
  textAlign: 'center',
  color: '#fff'
}

export const retryButtonSx: SxProps<Theme> = {
  textTransform: 'none',
  fontWeight: 600,
  borderRadius: '10px',
  color: '#fff',
  backgroundColor: GREEN,
  '&:hover': { backgroundColor: GREEN_DARK }
}

export const secondaryActionSx: SxProps<Theme> = {
  textTransform: 'none',
  color: '#d1d5db'
}

/** Boton para prender/apagar la linterna a mano. */
export const torchButtonSx = (on: boolean): SxProps<Theme> => ({
  position: 'absolute',
  bottom: 84,
  left: '50%',
  transform: 'translateX(-50%)',
  width: 56,
  height: 56,
  border: '1px solid',
  borderColor: on ? '#fbbf24' : 'rgba(255, 255, 255, 0.5)',
  backgroundColor: on ? 'rgba(251, 191, 36, 0.22)' : 'rgba(0, 0, 0, 0.45)',
  color: on ? '#fbbf24' : '#fff',
  '&:hover': {
    backgroundColor: on ? 'rgba(251, 191, 36, 0.32)' : 'rgba(0, 0, 0, 0.6)'
  }
})

/** Overlay del modo diagnostico (prop `debug`). No se usa en produccion. */
export const debugPanelSx: SxProps<Theme> = {
  position: 'absolute',
  top: 8,
  left: 8,
  right: 8,
  px: 1.5,
  py: 1,
  borderRadius: '8px',
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  color: '#fff',
  fontFamily: 'monospace',
  fontSize: '11px',
  lineHeight: 1.5,
  wordBreak: 'break-all',
  pointerEvents: 'none'
}
